// components/oeffentlich/ausschreibungTeile.jsx — gemeinsame Teile der
// öffentlichen Veranstaltungsübersicht und der Ausschreibung (22.10.2026,
// Muster docs/muster-ausschreibung/muster.html).
//
// Ob die Anmeldung offen ist, rechnet NUR der Server
// (backend/utils/anmeldeZustand.js, Feld `anmeldung`). Hier steht nur, wie
// ein Zustand aussieht: Farbe, Knopftext. Keine Emoji als Bedeutungsträger:
// Status = farbiger Punkt + Wort, Zeichen = Linien-Symbole.
import React, { useState, useCallback } from 'react';

export const ICON = {
  kal: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  ort: <><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></>,
  uhr: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  such: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  pfeil: <path d="M9 6l6 6-6 6" />,
  links: <path d="M15 6l-6 6 6 6" />,
  liste: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />,
  karten: <><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></>,
  euro: <path d="M17 6.5A7 7 0 1 0 17 17.5M4 10h9M4 14h9" />,
  haus: <path d="M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6" />,
  gruppe: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-4-6" /></>,
  regel: <><path d="M4 4h12l4 4v12H4z" /><path d="M8 12h8M8 16h5M8 8h4" /></>,
  schild: <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />,
  post: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
  tel: <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />,
  datei: <><path d="M14 3H6v18h12V7z" /><path d="M14 3v4h4" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7.5h.01" /></>,
  bett: <><path d="M3 18V7M3 14h18v4M21 14v-2a3 3 0 0 0-3-3h-7v5" /><circle cx="7" cy="11" r="1.6" /></>,
  waage: <path d="M12 4v16M5 20h14M6 8h12M6 8l-3 6h6zM18 8l-3 6h6z" />,
  live: <><circle cx="12" cy="12" r="2" /><path d="M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7M5.6 5.6a9 9 0 0 0 0 12.8M18.4 5.6a9 9 0 0 1 0 12.8" /></>,
  mond: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />,
  sonne: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
  web: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>,
};

export function Icon({ n, className = '' }) {
  return <svg className={`au-svg ${className}`} viewBox="0 0 24 24" aria-hidden="true">{ICON[n]}</svg>;
}

// Zustand (Server) → Farbe des Abzeichens
const KLASSE = {
  offen: 'offen', bald: 'bald', knapp: 'knapp', voll: 'voll', zu: 'zu', laeuft: 'laeuft',
  erg: 'erg', vorbei: 'zu', abgesagt: 'zu', entwurf: 'zu',
};

export function StatusAbzeichen({ anmeldung }) {
  if (!anmeldung) return null;
  return <span className={`au-st au-st-${KLASSE[anmeldung.zustand] || 'zu'}`}>{anmeldung.text}</span>;
}

export const istVorbei = (a) => a && (a.zustand === 'erg' || a.zustand === 'vorbei');

/** Knopf der Ausschreibung je Zustand. `ziel`: 'anmelden' | 'live' | null (aus). */
export function knopfFuer(anmeldung) {
  const z = anmeldung?.zustand;
  if (anmeldung?.anmeldbar) return { text: 'Jetzt anmelden', ziel: 'anmelden' };
  if (z === 'laeuft') return { text: 'Live-Ergebnisse', ziel: 'live' };
  if (z === 'erg') return { text: 'Ergebnisse ansehen', ziel: 'live' };
  if (z === 'voll') return { text: 'Ausgebucht', ziel: null };
  if (z === 'abgesagt') return { text: 'Abgesagt', ziel: null };
  return { text: 'Anmeldung geschlossen', ziel: null };
}

// ── Datum ohne Zeitzone: 'YYYY-MM-DD' wird als Ortsdatum gelesen ──
export const WT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
export const WT_LANG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
export const MON = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
export const MON_LANG = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export function tag(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}
export const d2 = (n) => String(n).padStart(2, '0');
export const datKurz = (d) => (d ? `${d2(d.getDate())}.${d2(d.getMonth() + 1)}.${d.getFullYear()}` : '');
export const datLang = (d) => (d ? `${WT_LANG[d.getDay()]}, ${d.getDate()}. ${MON_LANG[d.getMonth()]} ${d.getFullYear()}` : '');
export const euro = (x) => {
  const n = Number(x);
  if (!Number.isFinite(n)) return '';
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })} €`;
};

// ── Hell/dunkel: folgt dem System, der Schalter merkt sich die Wahl ──
const THEMA_SCHLUESSEL = 'tda-oeffentlich-thema';
function themaLesen() {
  try {
    const t = localStorage.getItem(THEMA_SCHLUESSEL);
    return t === 'hell' || t === 'dunkel' ? t : null;
  } catch { return null; }
}
export function useThema() {
  const [thema, setThema] = useState(themaLesen);
  const wirksam = thema || (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dunkel' : 'hell');
  const umschalten = useCallback(() => {
    const neu = wirksam === 'dunkel' ? 'hell' : 'dunkel';
    try { localStorage.setItem(THEMA_SCHLUESSEL, neu); } catch { /* ohne Speicher: nur diese Sitzung */ }
    setThema(neu);
  }, [wirksam]);
  return { thema, wirksam, umschalten };
}

export function ThemaKnopf({ wirksam, umschalten }) {
  return (
    <button type="button" className="au-thema" onClick={umschalten} aria-label="Hell oder dunkel umschalten"
      title={wirksam === 'dunkel' ? 'Helle Ansicht' : 'Dunkle Ansicht'}>
      <Icon n={wirksam === 'dunkel' ? 'sonne' : 'mond'} />{wirksam === 'dunkel' ? 'Hell' : 'Dunkel'}
    </button>
  );
}

/** Logo einer Veranstaltung: Turnierlogo, sonst Verbandslogo, sonst TDA. */
export const logoFuer = (t) => t?.logo_url || t?.verband_logo_url || '/logo.webp';

export function OeffentlicherFuss() {
  return (
    <footer className="au-fuss">
      <span>
        <a href="https://tda-intl.org">TDA International</a>
        <a href="https://tda-intl.org/datenschutz">Datenschutz</a>
        <a href="https://tda-intl.org/impressum">Impressum</a>
      </span>
      <span>© {new Date().getFullYear()} TDA International · events.tda-intl.org</span>
    </footer>
  );
}
